<template>
  <div>
    <div v-if="isMobile && showDrawer" class="mobile-menu-bar" @click="drawer = true">
      <div class="mobile-menu-content">
        <BrandLogo variant="symbol" class="mobile-logo" />
        <span class="mobile-menu-title">{{ workspaceTitle }}</span>
        <v-icon size="22" class="mobile-menu-icon">mdi-menu</v-icon>
      </div>
    </div>
    <v-navigation-drawer
      v-if="showDrawer"
      v-model="drawer"
      app
      :rail="drawerRail"
      :rail-width="72"
      :permanent="!isMobile"
      :temporary="isMobile"
      :expand-on-hover="desktopHoverExpandEnabled"
      width="240"
      :class="['nav-drawer', { 'nav-drawer--collapsed': isRail, 'nav-drawer--home': isHomeRoute }]"
      @mouseenter="handleEnter"
      @mouseleave="handleLeave"
    >
    <div class="nav-header" v-if="!isMobile" :class="{ 'nav-header--rail': isRail }">
      <div class="brand" :class="{ 'brand--rail': isRail }">
        <div class="brand-logo-wrap">
          <BrandLogo variant="symbol" class="brand-logo" />
        </div>
        <div v-if="!isRail" class="brand-text">
          <span class="brand-name" :title="workspaceTitle">{{ workspaceTitle }}</span>
          <span class="brand-sub">{{ $t("navigation.workspace") }}</span>
        </div>
      </div>
      <button
        v-if="!isRail"
        class="nav-collapse-btn"
        :aria-label="desktopToggleLabel"
        @click.stop="toggleDesktopPin"
      >
        <v-icon size="16">{{ desktopToggleIcon }}</v-icon>
      </button>
    </div>

    <v-divider class="mb-2"></v-divider>

    <!-- Main nav items -->
    <v-list
      v-if="isLoggedIn"
      density="comfortable"
      nav
      class="nav-list"
    >
      <!-- ── HR Dashboard context ───────────────────────── -->
      <template v-if="isHrDashboardRoute">
        <!-- Back to main -->
        <v-tooltip location="right" :disabled="!isRail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              value="hr-back-main"
              class="nav-item"
              @click="navigate('/')"
            >
              <template #prepend>
                <v-icon size="22">mdi-view-grid-plus-outline</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t('hrDashboard.menuItems.mainDashboard.label') }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t('hrDashboard.menuItems.mainDashboard.label') }}</span>
        </v-tooltip>
        <v-divider class="my-1" />
        <!-- HR-specific items -->
        <v-tooltip
          v-for="item in hrMenuItems"
          :key="item.id"
          location="right"
          :disabled="!isRail"
        >
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              :value="item.id"
              :active="isHrItemActive(item)"
              :class="{ 'nav-item--active': isHrItemActive(item) }"
              @click="navigateHrOption(item)"
              class="nav-item"
            >
              <template #prepend>
                <v-icon size="22">{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t(item.titleKey) }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t(item.titleKey) }}</span>
        </v-tooltip>
      </template>

      <!-- ── External Dashboard context ──────────────────── -->
      <template v-else-if="isExternalDashboardRoute">
        <!-- Back to main -->
        <v-tooltip location="right" :disabled="!isRail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              value="ext-back-main"
              class="nav-item"
              @click="navigate('/')"
            >
              <template #prepend>
                <v-icon size="22">mdi-view-grid-plus-outline</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t('externalDashboard.menuItems.mainDashboard.label') }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t('externalDashboard.menuItems.mainDashboard.label') }}</span>
        </v-tooltip>
        <v-divider class="my-1" />
        <!-- Dashboard-specific items -->
        <v-tooltip
          v-for="item in extDashboardMenuItems"
          :key="item.id"
          location="right"
          :disabled="!isRail"
        >
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              :value="item.id"
              :active="isExtDashboardItemActive(item)"
              :class="{ 'nav-item--active': isExtDashboardItemActive(item) }"
              @click="navigateExtOption(item)"
              class="nav-item"
            >
              <template #prepend>
                <v-icon size="22">{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t(item.titleKey) }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t(item.titleKey) }}</span>
        </v-tooltip>
      </template>

      <!-- ── Regular nav items ──────────────────────────── -->
      <template v-else>
        <v-tooltip
          v-for="item in filteredMenuItems"
          :key="item.route + '-' + item.titleKey"
          location="right"
          :disabled="!isRail"
        >
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              :value="item.route + '-' + item.titleKey"
              :active="isActive(item)"
              :class="{ 'nav-item--active': isActive(item) }" :data-tour-nav="item.route"
              @click="navigate(item.route)"
              class="nav-item"
            >
              <template #prepend>
                <v-badge
                  v-if="shouldShowNcrmActivityBadge(item)"
                  :content="ncrmActivityBadgeContent"
                  :color="ncrmActivityBadgeColor"
                  offset-x="4"
                  offset-y="4"
                  floating
                >
                  <v-icon size="22">{{ item.icon }}</v-icon>
                </v-badge>
                <v-icon v-else size="22">{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t(item.titleKey) }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t(item.titleKey) }}</span>
        </v-tooltip>
      </template>
    </v-list>

    <!-- Bottom items — anchored to drawer bottom via #append slot -->
    <template #append>
      <v-list v-if="isLoggedIn" density="comfortable" nav class="nav-list nav-list--bottom">
        <v-divider class="mb-2"></v-divider>
        <NcrmNotificationNavItem v-if="isNcrmRoute && canSeeNcrmActivityNotifications" :rail="isRail" :mobile="isMobile" @close-navigation="drawer = false" />
        <v-tooltip v-if="!isAdminRoute" location="right" :disabled="!isRail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              class="nav-item"
              :value="isDark ? 'theme-light' : 'theme-dark'"
              @click="toggleTheme"
            >
              <template #prepend>
                <v-icon size="22">{{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ isDark ? $t('navigation.lightMode') : $t('navigation.darkMode') }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ isDark ? $t('navigation.lightMode') : $t('navigation.darkMode') }}</span>
        </v-tooltip>

        <v-tooltip location="right" :disabled="!isRail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item v-bind="tooltipProps" class="nav-item danger" @click="logout" value="logout">
              <template #prepend>
                <v-icon size="22">mdi-logout</v-icon>
              </template>
              <v-list-item-title class="nav-label">{{ $t("navigation.logout") }}</v-list-item-title>
            </v-list-item>
          </template>
          <span>{{ $t("navigation.logout") }}</span>
        </v-tooltip>
      </v-list>
    </template>
    </v-navigation-drawer>

  </div>
</template>

<script>
import { ref, watch, computed, onMounted, onBeforeUnmount } from "vue";
import { useDisplay, useTheme } from "vuetify";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { expandRoleAliases, DEFAULT_ROLE_KEYS } from "@/Utile/roleHelpers.mjs";
import {
  buildNavigationDrawerItems,
  buildExternalDashboardMenu,
  buildHrDashboardMenu,
  hasHrEmployeeContext,
} from "@/Utile/navigationDrawerMenu.mjs";
import { decodeTokenPayload, resolveUserCompanyId, resolveUserEmployeeId, resolveSelectedHrEmployeeId, storeUserEmployeeId, getRolesSetFromStorage, hasAuthSession } from "@/Utile/navigationDrawerAuth.mjs";
import {
  canAccessModule,
  readStoredLicenseSnapshot,
  storeLicenseSnapshot,
} from "@/Utile/licenseHelpers.mjs";
import { isAxiosRequestAborted } from "@/Utile/httpErrors.mjs";
import { stopSmartTrackerOnLogout } from "@/Utile/smartTrackerRuntime.mjs";
import { clearAuthStorage, redirectToLoginPage } from "@/Utile/authStorage.mjs";
import { useNavigationDrawerContext } from "@/Utile/navigationDrawerContext.mjs";
import { useCurrentCompany, setCurrentCompanyName, setCurrentUserName } from "@/Utile/currentUserCompany.mjs";
import BrandLogo from "@/components/brand/BrandLogo.vue";
import NcrmNotificationNavItem from "@/components/ncrm/notifications/NcrmNotificationNavItem.vue";
export default {
  name: "NavigationDrawer",
  components: { BrandLogo, NcrmNotificationNavItem },
  setup() {
    const { t } = useI18n({ useScope: "global" });
    const { companyName } = useCurrentCompany();
    const workspaceTitle = computed(() => companyName.value || t("navigation.brand"));
    const { smAndDown } = useDisplay();
    const theme = useTheme();
    const isDark = computed(() => theme.global.name.value === "dark");
    const toggleTheme = () => {
      const next = isDark.value ? "light" : "dark";
      theme.global.name.value = next;
      localStorage.setItem("app-theme", next);
    };
    const isMobile = smAndDown;
    const drawer = ref(!isMobile.value);
    const desktopHovered = ref(false);
    const desktopPinnedOpen = ref(false);
    const drawerRail = computed(() => {
      if (isMobile.value) return false;
      return !desktopPinnedOpen.value;
    });
    const desktopHoverExpandEnabled = computed(() => {
      if (isMobile.value) return false;
      return !desktopPinnedOpen.value;
    });
    const isRail = computed(() => {
      if (isMobile.value) return false;
      return !(desktopPinnedOpen.value || desktopHovered.value);
    });
    const isLoggedIn = ref(hasAuthSession());
    const router = useRouter();
    const userRole = ref(localStorage.getItem("user-role"));
    const ncrmActivityNotifications = ref({
      overdueCount: 0,
      upcomingCount: 0,
      badgeCount: 0,
      hasNotifications: false,
      loading: false,
    });
    let ncrmNotificationsTimer = null;

    const rolesVersion = ref(0);
    const bumpRoles = () => {
      rolesVersion.value += 1;
    };

    const baseRoles = computed(() => {
      // eslint-disable-next-line no-unused-expressions
      rolesVersion.value;
      return getRolesSetFromStorage();
    });

    const normalizedRoles = computed(() => {
      const expanded = new Set();
      baseRoles.value.forEach((norm) => {
        expandRoleAliases(norm).forEach((alias) => expanded.add(alias));
      });
      return expanded;
    });

    const licenseSnapshot = computed(() => {
      // eslint-disable-next-line no-unused-expressions
      rolesVersion.value;
      return readStoredLicenseSnapshot(decodeTokenPayload());
    });

    const hasNcrmCompanyContext = computed(() => {
      // eslint-disable-next-line no-unused-expressions
      rolesVersion.value;
      return resolveUserCompanyId() !== null;
    });

    const { rememberNavContextForPath, isNcrmContextRoute, isHrContextRoute } = useNavigationDrawerContext(globalThis.sessionStorage);
    const isNcrmRoute = computed(() => isNcrmContextRoute(router.currentRoute.value?.path || ""));
    const isHomeRoute = computed(() => (router.currentRoute.value?.path || "") === "/");
    const isAdminRoute = computed(() => /^\/admin(?:\/|$)/.test(router.currentRoute.value?.path || ""));
    const isHrDashboardRoute = computed(() => isHrContextRoute(router.currentRoute.value?.path || ""));

    const isExternalDashboardRoute = computed(() => {
      const path = router.currentRoute.value?.path || "";
      return path === "/dashboard" || path.startsWith("/dashboard/");
    });

    const extDashboardActiveOption = computed(() => {
      if (!isExternalDashboardRoute.value) return null;
      const opt = router.currentRoute.value?.query?.option;
      return typeof opt === "string" ? opt.toLowerCase() : null;
    });

    // Ruta curentă e dependență reactivă, ca și la meniul HR: `user-employee-id` (localStorage) nu e urmărit de Vue.
    const extDashboardMenuItems = computed(() => buildExternalDashboardMenu(normalizedRoles.value, baseRoles.value, router.currentRoute.value && resolveUserEmployeeId()));

    const isExtDashboardItemActive = (item) => extDashboardActiveOption.value === item.id;

    const navigateExtOption = (item) => {
      router.push(item.route || { path: "/dashboard", query: { option: item.id } });
      if (isMobile.value) drawer.value = false;
    };

    // ── HR Dashboard context navigation ─────────────────────────
    const hrActiveOption = computed(() => {
      if (!isHrDashboardRoute.value) return null;
      const opt = router.currentRoute.value?.query?.option;
      return typeof opt === "string" ? opt.toLowerCase() : null;
    });

    // `router.currentRoute` e dependența reactivă: filele angajatului selectat apar/dispar la fiecare navigare.
    const hrMenuItems = computed(() => buildHrDashboardMenu(normalizedRoles.value, baseRoles.value, resolveUserEmployeeId(), { hasEmployeeContext: hasHrEmployeeContext(router.currentRoute.value, resolveSelectedHrEmployeeId()) }));

    const isHrItemActive = (item) => {
      if (item.route) {
        const current = router.currentRoute.value?.path || "";
        return current === item.route || current.startsWith(item.route + "/");
      }
      return hrActiveOption.value === item.id;
    };

    const navigateHrOption = (item) => {
      if (item.route) {
        router.push(item.route);
      } else {
        router.push({ path: "/hr-dashboard", query: { option: item.id } });
      }
      if (isMobile.value) drawer.value = false;
    };

    const canSeeNcrmActivityNotifications = computed(() => {
      if (!isLoggedIn.value) return false;
      if (!hasNcrmCompanyContext.value) return false;
      const roles = normalizedRoles.value;
      if (
        !canAccessModule("crm", {
          roleKeys: Array.from(baseRoles.value),
          licenseSnapshot: licenseSnapshot.value,
          fallbackIfUnknown: true,
        })
      ) {
        return false;
      }
      return (
        roles.has("admin") ||
        roles.has("miniadmin") ||
        roles.has("crm-admin") ||
        roles.has("crm-operator") ||
        roles.has("crm-caller") ||
        roles.has("caller")
      );
    });
    const ncrmActivityBadgeCount = computed(() =>
      Number(ncrmActivityNotifications.value.badgeCount || 0)
    );
    const ncrmActivityBadgeContent = computed(() => {
      const count = ncrmActivityBadgeCount.value;
      if (count <= 0) return "";
      return count > 99 ? "99+" : String(count);
    });
    const ncrmActivityBadgeColor = "grey-darken-1";

    const filteredMenuItems = computed(() => {
      return buildNavigationDrawerItems({
        isAdminRoute: isAdminRoute.value,
        isNcrmRoute: isNcrmRoute.value,
        baseRoles: baseRoles.value,
        normalizedRoles: normalizedRoles.value,
        licenseSnapshot: licenseSnapshot.value,
      });
    });

    const isNcrmActivitiesItem = (item) => String(item?.route || "").trim() === "/ncrm/activities";

    const shouldShowNcrmActivityBadge = (item) =>
      isNcrmActivitiesItem(item) && ncrmActivityBadgeCount.value > 0;

    const fetchNcrmActivityNotifications = async () => {
      if (!canSeeNcrmActivityNotifications.value) {
        ncrmActivityNotifications.value = {
          overdueCount: 0,
          upcomingCount: 0,
          badgeCount: 0,
          hasNotifications: false,
          loading: false,
        };
        return;
      }
      ncrmActivityNotifications.value = {
        ...ncrmActivityNotifications.value,
        loading: true,
      };
      try {
        const { data } = await axios.get("/ncrm/activity/notifications/summary");
        ncrmActivityNotifications.value = {
          overdueCount: Number(data?.overdueCount || 0),
          upcomingCount: Number(data?.upcomingCount || 0),
          badgeCount: Number(data?.badgeCount || 0),
          hasNotifications: Boolean(data?.hasNotifications),
          loading: false,
        };
      } catch (error) {
        if (isAxiosRequestAborted(error)) {
          ncrmActivityNotifications.value = {
            ...ncrmActivityNotifications.value,
            loading: false,
          };
          return;
        }
        const statusCode = Number(error?.response?.status || 0);
        const backendMessage = String(error?.response?.data?.error || error?.response?.data?.message || "");
        const isMissingContext403 =
          statusCode === 403 && /(company_id\s+lipsa|user_id\s+lipsa)/i.test(backendMessage);
        if (isMissingContext403) {
          ncrmActivityNotifications.value = {
            overdueCount: 0,
            upcomingCount: 0,
            badgeCount: 0,
            hasNotifications: false,
            loading: false,
          };
          return;
        }
        console.error("[ncrm] activity notifications summary failed", error);
        ncrmActivityNotifications.value = {
          ...ncrmActivityNotifications.value,
          loading: false,
        };
      }
    };

    const showDrawer = computed(() => {
      const rawRoles = baseRoles.value;
      if (!rawRoles.size) return false;
      // Rute cu meniu propriu în drawer — vizibil pentru orice user logat. `isHrDashboardRoute`
      // include și rutele de recrutare, ca recruiter-default să aibă meniul de recrutare acolo.
      const path = router.currentRoute.value?.path || "";
      if (
        isHrDashboardRoute.value ||
        path === "/dashboard" ||
        path.startsWith("/dashboard/")
      ) {
        return true;
      }
      const hasNonDefault = [...rawRoles].some((r) => !DEFAULT_ROLE_KEYS.includes(r));
      const hasNonVacation = [...rawRoles].some((r) => !r.startsWith("vacation-"));
      if (!hasNonDefault || !hasNonVacation) {
        return false;
      }
      return true;
    });

    const navigate = (route) => {
      rememberNavContextForPath(route, { preserveShared: route === "/me" && isNcrmRoute.value });
      router.push(route);
      if (isMobile.value) {
        drawer.value = false;
      }
    };

    // Pick the single most-specific (longest) matching nav item so that
    // e.g. /ncrm/onboarding does NOT also light up /ncrm (CRM Board).
    const activeNavRoute = computed(() => {
      const current = router.currentRoute.value?.path || "";
      let best = null;
      for (const item of filteredMenuItems.value) {
        if (!item.route) continue;
        const matches = current === item.route || current.startsWith(item.route + "/");
        if (matches && (!best || item.route.length > best.length)) {
          best = item.route;
        }
      }
      return best;
    });

    const isActive = (item) => item.route === activeNavRoute.value;

    const logout = async () => {
      await stopSmartTrackerOnLogout();
      clearAuthStorage();
      isLoggedIn.value = false;
      bumpRoles();
      redirectToLoginPage();
      drawer.value = !isMobile.value;
    };

    const handleEnter = () => {
      if (isMobile.value) return;
      desktopHovered.value = true;
    };

    const handleLeave = () => {
      if (isMobile.value) return;
      desktopHovered.value = false;
    };

    const toggleDesktopPin = () => {
      if (isMobile.value) return;
      desktopPinnedOpen.value = !desktopPinnedOpen.value;
    };

    watch(isMobile, (newVal) => {
      drawer.value = !newVal;
      desktopHovered.value = false;
      desktopPinnedOpen.value = false;
    });

    // Ruta inițială (încărcare directă / reload) nu declanșează watcher-ul de mai jos.
    rememberNavContextForPath(router.currentRoute.value?.path || "", { preserveShared: true });

    watch(() => router.currentRoute.value, () => {
      isLoggedIn.value = hasAuthSession();
      userRole.value = localStorage.getItem("user-role");
      rememberNavContextForPath(router.currentRoute.value?.path || "", { preserveShared: true });
      if (isNcrmRoute.value) {
        fetchNcrmActivityNotifications();
      }
    });

    watch(canSeeNcrmActivityNotifications, (enabled) => {
      if (!enabled) {
        ncrmActivityNotifications.value = {
          overdueCount: 0,
          upcomingCount: 0,
          badgeCount: 0,
          hasNotifications: false,
          loading: false,
        };
        return;
      }
      fetchNcrmActivityNotifications();
    });

    const refreshRolesFromProfile = async () => {
      if (!hasAuthSession()) return;
      try {
        const { data } = await axios.get("/me");
        if (data?.roles) {
          localStorage.setItem("user-roles", JSON.stringify(data.roles));
        }
        if (data?.primary_role) {
          localStorage.setItem("user-role", data.primary_role);
          userRole.value = data.primary_role;
        } else if (data?.role) {
          localStorage.setItem("user-role", data.role);
          userRole.value = data.role;
        }
        if (data?.company_id) {
          localStorage.setItem("user-company-id", String(data.company_id));
        } else if (data && Object.prototype.hasOwnProperty.call(data, "company_id")) {
          localStorage.removeItem("user-company-id");
        }
        storeUserEmployeeId(data);
        setCurrentCompanyName(data?.company_name);
        setCurrentUserName(data?.name);
        storeLicenseSnapshot(data);
        bumpRoles();
      } catch (err) {
        console.error("Nu s-au putut încărca rolurile din /me", err);
      }
    };

    onMounted(async () => {
      if (isLoggedIn.value) {
        await refreshRolesFromProfile();
      }
      if (canSeeNcrmActivityNotifications.value) {
        await fetchNcrmActivityNotifications();
      }
      ncrmNotificationsTimer = setInterval(() => {
        if (canSeeNcrmActivityNotifications.value) {
          fetchNcrmActivityNotifications();
        }
      }, 2 * 60 * 1000);
    });

    onBeforeUnmount(() => {
      if (ncrmNotificationsTimer) {
        clearInterval(ncrmNotificationsTimer);
        ncrmNotificationsTimer = null;
      }
    });

    const desktopToggleIcon = computed(() =>
      isRail.value ? "mdi-chevron-double-right" : "mdi-chevron-double-left"
    );
    const desktopToggleLabel = computed(() =>
      isRail.value ? t("navigation.expand") : t("navigation.collapse")
    );
    return {
      t,
      companyName,
      workspaceTitle,
      drawer,
      isMobile,
      showDrawer,
      isLoggedIn,
      filteredMenuItems,
      navigate,
      logout,
      isActive,
      isHomeRoute,
      isAdminRoute,
      isNcrmRoute,
      isHrDashboardRoute,
      hrMenuItems,
      isHrItemActive,
      navigateHrOption,
      isExternalDashboardRoute,
      extDashboardMenuItems,
      isExtDashboardItemActive,
      navigateExtOption,
      isRail,
      drawerRail,
      desktopHoverExpandEnabled,
      handleEnter,
      handleLeave,
      toggleDesktopPin,
      desktopToggleIcon,
      desktopToggleLabel,
      shouldShowNcrmActivityBadge,
      ncrmActivityBadgeContent,
      ncrmActivityBadgeColor,
      canSeeNcrmActivityNotifications,
      isDark,
      toggleTheme,
      activeNavRoute,
    };
  },
};
</script>
<style scoped src="./NavigationDrawer.styles.css"></style>
